import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import styles from './Footer.module.css';
import logo from "./Logoras.webp";
import { BsWhatsapp, BsTelephone, BsInstagram, BsEnvelope, BsGeoAlt } from 'react-icons/bs';
import { FiArrowUpRight } from 'react-icons/fi';
import {
  PHONE_TEL,
  PHONE_DISPLAY,
  WHATSAPP_DISPLAY,
  EMAIL,
  INSTAGRAM_HANDLE,
  INSTAGRAM_URL,
  waLink,
  mailtoLink,
  trackLead,
  LeadChannel,
} from '../../data/contact';

const navLinks = [
  { name: 'Home',       href: '/' },
  { name: 'About Us',   href: '/about' },
  { name: 'Services',   href: '/services' },
  { name: 'Products',   href: '/products' },
  { name: 'Gallery',    href: '/gallery' },
  { name: 'Contact',    href: '/contact' },
];

/** The footer sits on every page, so its lead links carry the current path. */
const buildContactLinks = (source: string): {
  icon: React.ReactNode; label: string; value: string; href: string; channel: LeadChannel;
}[] => [
  { icon: <BsWhatsapp size={15} />,  label: 'WhatsApp',  value: WHATSAPP_DISPLAY,  channel: 'whatsapp',
    href: waLink("Hi Rashed Al Shamsi, I'd like to enquire about your printing and corporate gift services.", source) },
  { icon: <BsTelephone size={15} />, label: 'Call',      value: PHONE_DISPLAY,     channel: 'phone',
    href: `tel:${PHONE_TEL}` },
  { icon: <BsInstagram size={15} />, label: 'Instagram', value: INSTAGRAM_HANDLE,  channel: 'instagram',
    href: INSTAGRAM_URL },
  { icon: <BsEnvelope size={15} />,  label: 'Email',     value: EMAIL,             channel: 'email',
    href: mailtoLink('Enquiry from your website', source, 'Hi Rashed Al Shamsi,\n\n') },
];

const Footer: React.FC = () => {
  const { pathname } = useLocation();
  const source = `Footer · ${pathname}`;
  const contactLinks = buildContactLinks(source);

  return (
  <footer className={styles.footer}>

    {/* Top CTA strip */}
    <div className={styles.cta}>
      <div className={styles.ctaText}>
        <h3>Ready to start your next project?</h3>
        <p>Let's create something great together.</p>
      </div>
      <Link to="/contact" className={styles.ctaBtn}>
        Get in Touch <FiArrowUpRight size={18} />
      </Link>
    </div>

    {/* Main grid */}
    <div className={styles.main}>

      {/* Brand column */}
      <div className={styles.brandCol}>
        <div className={styles.brand}>
          <img src={logo} alt="Rashed Al Shamsi Advertising Gifts logo" className={styles.logo} />
          <span className={styles.company}>Rashed Al Shamsi<br />Advertising Gifts</span>
        </div>
        <p className={styles.tagline}>
          Your trusted partner for custom branding, printing, and promotional gifts in Dubai.
        </p>
        <div className={styles.socials}>
          <a href={waLink("Hi Rashed Al Shamsi, I'd like to enquire about your printing and corporate gift services.", source)}
             aria-label="WhatsApp" className={styles.socialBtn} target="_blank" rel="noopener noreferrer"
             onClick={() => trackLead('whatsapp', source)}><BsWhatsapp size={17} /></a>
          <a href={INSTAGRAM_URL} aria-label="Instagram" className={styles.socialBtn} target="_blank" rel="noopener noreferrer"
             onClick={() => trackLead('instagram', source)}><BsInstagram size={17} /></a>
          <a href={mailtoLink('Enquiry from your website', source, 'Hi Rashed Al Shamsi,\n\n')}
             aria-label="Email" className={styles.socialBtn}
             onClick={() => trackLead('email', source)}><BsEnvelope size={17} /></a>
        </div>
      </div>

      {/* Nav column */}
      <div className={styles.col}>
        <h4 className={styles.colTitle}>Quick Links</h4>
        <nav className={styles.navList}>
          {navLinks.map(l => (
            <Link key={l.name} to={l.href} className={styles.navLink}>{l.name}</Link>
          ))}
        </nav>
      </div>

      {/* Contact column */}
      <div className={styles.col}>
        <h4 className={styles.colTitle}>Contact Us</h4>
        <div className={styles.contactList}>
          {contactLinks.map(c => (
            <a
              key={c.label}
              href={c.href}
              className={styles.contactItem}
              target={c.href.startsWith('http') ? '_blank' : undefined}
              rel="noopener noreferrer"
              onClick={() => trackLead(c.channel, source)}
            >
              <span className={styles.contactIcon}>{c.icon}</span>
              <span className={styles.contactVal}>{c.value}</span>
            </a>
          ))}
        </div>
      </div>

    </div>

    {/* Bottom bar */}
    <div className={styles.bottom}>
      <div className={styles.location}>
        <BsGeoAlt size={13} />
        <span>Abdul Aziz Al Majid, 19A St, G Floor Shop 14 — Deira, Al Rigga, Dubai</span>
      </div>
      <p className={styles.copyright}>
        © {new Date().getFullYear()} Rashed Al Shamsi Advertising Gifts. All rights reserved.
      </p>
    </div>

  </footer>
  );
};

export default Footer;
